import { useQuery } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { enhancedStockInfo } from "../utils/mockStockData";
import { useContactGate } from "./useContactId";
import { computeStockStatus } from "../utils/stockStatus";
import {
  getVariantColorKey,
  resolveVariantColorAndSize,
} from "../utils/variantParsing";

const MOCK_STOCK_FLAG_KEY = "wwgStoreUseMockStockData";

function shouldUseMockStockData() {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(MOCK_STOCK_FLAG_KEY) === "true";
}

function normalizeIsLiked(isLiked, contactId) {
  if (typeof isLiked === "boolean") return isLiked;

  if (isLiked && typeof isLiked === "object") {
    const contactKey = String(contactId ?? "");
    return Boolean(isLiked[contactKey]);
  }

  return Boolean(isLiked);
}

function normalizeNote(notes, contactId) {
  if (typeof notes === "string") return notes;
  if (!notes || typeof notes !== "object") return "";
  const note = notes[String(contactId ?? "")];
  return typeof note === "string" ? note : "";
}

function normalizeAttributeToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
}

function deriveAttributeFlags(attributes = []) {
  const tokens = new Set();

  (Array.isArray(attributes) ? attributes : []).forEach((attribute) => {
    tokens.add(normalizeAttributeToken(attribute?.type));
    tokens.add(normalizeAttributeToken(attribute?.code));
    tokens.add(normalizeAttributeToken(attribute?.label));
  });

  const has = (token) => tokens.has(token);

  return {
    is3rdParty:
      has("SOURCE_3RD_PARTY") ||
      has("3RD_PARTY") ||
      has("THIRD_PARTY") ||
      has("SOURCE_THIRD_PARTY"),
    isHot: has("HOT") || has("IS_HOT") || has("TAG_HOT"),
    isNew: has("NEW") || has("IS_NEW") || has("TAG_NEW") || has("NEW_RELEASE"),
  };
}

function toBooleanLike(value) {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return ["true", "1", "yes", "y"].includes(normalized);
}

function deriveItemFlags(item, attributeFlags) {
  const sourceToken = normalizeAttributeToken(item?.source);
  const sourceLabelToken = normalizeAttributeToken(item?.sourceLabel);

  const is3rdPartyFromSource =
    sourceToken === "SOURCE_3RD_PARTY" ||
    sourceToken === "3RD_PARTY" ||
    sourceLabelToken === "3RD_PARTY" ||
    sourceLabelToken === "THIRD_PARTY";

  return {
    is3rdParty: Boolean(
      toBooleanLike(item?.is3rdParty) ||
        toBooleanLike(item?.isThirdParty) ||
        toBooleanLike(item?.thirdParty) ||
        attributeFlags.is3rdParty ||
        is3rdPartyFromSource
    ),
    isHot: Boolean(
      toBooleanLike(item?.isHot) ||
        toBooleanLike(item?.hot) ||
        attributeFlags.isHot
    ),
    isNew: Boolean(
      toBooleanLike(item?.isNew) ||
        toBooleanLike(item?.new) ||
        attributeFlags.isNew
    ),
  };
}

// Variant-level flags (is3rdParty/isHot/isTrending/isNew) mirror the product-level flags but
// are matched per (variationId, variantId) by the backend. Data-model-only for now: no
// StockItem/ColumnStockItem badge rendering reads these yet, but the fields are always present
// so the UI can opt in later without another backend round-trip.
function normalizeVariantFlags(variant) {
  return {
    is3rdParty: Boolean(toBooleanLike(variant?.is3rdParty)),
    isHot: Boolean(toBooleanLike(variant?.isHot)),
    isTrending: Boolean(toBooleanLike(variant?.isTrending)),
    isNew: Boolean(toBooleanLike(variant?.isNew)),
  };
}

function normalizeItemVariants(item) {
  const variants = Array.isArray(item?.variants) ? item.variants : null;
  if (!variants) return variants;
  return variants.map((variant) => ({
    ...variant,
    ...normalizeVariantFlags(variant),
  }));
}

function deriveVariantMetadata(item) {
  const variants = Array.isArray(item?.variants) ? item.variants : [];
  if (variants.length === 0) {
    return {
      detailColors: Array.isArray(item?.detailColors) ? item.detailColors : [],
      sizeOptions: Array.isArray(item?.sizeOptions) ? item.sizeOptions : [],
      variantMatrix: item?.variantMatrix || {},
    };
  }

  const colorImageMap = new Map();
  const colorAvailability = new Map();
  const sizeSellability = new Map();
  const variantMatrix = {};

  // A variant is unavailable if the backend marked it unsellable OR its
  // per-variant stock tracking (isInStock, from the func-bsm-api stock
  // enforcement change) says it's out of stock. Missing isInStock (e.g. the
  // list-route StockGet response, which doesn't populate per-variant stock
  // fields) must not be treated as out of stock - only an explicit `false`.
  variants.forEach((variant) => {
    const { color, size } = resolveVariantColorAndSize(variant);
    const normalizedColor = String(color || "").trim();
    const normalizedSize = String(size || "").trim();
    const isAvailable =
      variant?.isSellable !== false && variant?.isInStock !== false;

    const colorKey = getVariantColorKey(variant, normalizedColor);

    if (normalizedColor && !colorImageMap.has(colorKey)) {
      colorImageMap.set(colorKey, {
        label: normalizedColor,
        imageUrl: variant?.imageUrl || item?.imageUrl || null,
        images: Array.isArray(variant?.images) ? variant.images : [],
        swatchHex: variant?.swatchHex || null,
        swatchImage: variant?.swatchImage || null,
      });
    }

    if (normalizedColor) {
      const wasAvailable = colorAvailability.get(colorKey);
      colorAvailability.set(
        colorKey,
        typeof wasAvailable === "boolean"
          ? wasAvailable || isAvailable
          : isAvailable
      );
    }

    if (normalizedSize) {
      const wasSellable = sizeSellability.get(normalizedSize);
      sizeSellability.set(
        normalizedSize,
        typeof wasSellable === "boolean"
          ? wasSellable || isAvailable
          : isAvailable
      );
    }

    if (normalizedColor || normalizedSize) {
      variantMatrix[`${normalizedColor}::${normalizedSize}`] = {
        variantId: variant?.variantId ?? null,
        variationId: variant?.variationId ?? null,
        isAvailable,
        // Carried through so the details page can compute the *selected*
        // variant's low-stock/out-of-stock status the same way the list
        // view does (via computeStockStatus), instead of relying on the
        // product-level fields which are meaningless for CONFIGURABLE items
        // (the API sends isInStock/quantityAvailable per-variant only).
        isInStock: variant?.isInStock,
        quantityOnHand: variant?.quantityOnHand ?? null,
        quantityAvailable: variant?.quantityAvailable ?? null,
        lowStockThreshold: variant?.lowStockThreshold ?? null,
      };
    }
  });

  const derivedColors = Array.from(colorImageMap.entries())
    .filter(([, option]) => option?.label)
    .map(([colorKey, option]) => ({
      ...option,
      isSoldOut: !(colorAvailability.get(colorKey) ?? true),
    }));
  const derivedSizes = Array.from(sizeSellability.entries()).map(
    ([label, isAvailable]) => ({
      label,
      isSoldOut: !isAvailable,
    })
  );

  return {
    detailColors:
      derivedColors.length > 0
        ? derivedColors
        : Array.isArray(item?.detailColors)
        ? item.detailColors
        : [],
    sizeOptions:
      derivedSizes.length > 0
        ? derivedSizes
        : Array.isArray(item?.sizeOptions)
        ? item.sizeOptions
        : [],
    variantMatrix:
      Object.keys(variantMatrix).length > 0
        ? variantMatrix
        : item?.variantMatrix || {},
  };
}

function normalizeStockItem(item, contactId) {
  if (!item || typeof item !== "object") return item;

  const attributeFlags = deriveAttributeFlags(item?.attributes);
  const itemFlags = deriveItemFlags(item, attributeFlags);
  const variantMetadata = deriveVariantMetadata(item);
  const variants = normalizeItemVariants(item);

  return {
    ...item,
    ...variantMetadata,
    ...computeStockStatus(item),
    variants,
    isLiked: normalizeIsLiked(item.isLiked, contactId),
    note: normalizeNote(item.notes, contactId),
    is3rdParty: itemFlags.is3rdParty,
    isHot: itemFlags.isHot,
    isNew: itemFlags.isNew,
  };
}

function categoryRank(category) {
  const normalized = String(category || "")
    .trim()
    .toLowerCase();
  if (normalized === "literature") return 3;
  if (!normalized) return 0;
  return 1;
}

function buildStockIdentity(item) {
  const stockId = item?.stockId ?? "";
  const productId = item?.productId ?? "";
  return `${stockId}::${productId}`;
}

function mergeAttributes(existingAttributes = [], incomingAttributes = []) {
  const merged = new Map();
  [...existingAttributes, ...incomingAttributes].forEach((attribute) => {
    const key = `${attribute?.type ?? ""}::${attribute?.code ?? ""}::${
      attribute?.label ?? ""
    }`;
    if (!merged.has(key)) {
      merged.set(key, attribute);
    }
  });
  return Array.from(merged.values());
}

function mergeDuplicateStockItems(existing, incoming) {
  const existingRank = categoryRank(existing?.category);
  const incomingRank = categoryRank(incoming?.category);
  const preferred = incomingRank > existingRank ? incoming : existing;
  const other = preferred === incoming ? existing : incoming;

  return {
    ...preferred,
    attributes: mergeAttributes(existing?.attributes, incoming?.attributes),
    is3rdParty: Boolean(existing?.is3rdParty || incoming?.is3rdParty),
    isHot: Boolean(existing?.isHot || incoming?.isHot),
    isNew: Boolean(existing?.isNew || incoming?.isNew),
    // Keep a usable image if the preferred row is missing one.
    imageUrl: preferred?.imageUrl || other?.imageUrl,
  };
}

function dedupeStockItems(stockItems = []) {
  const seen = new Map();

  stockItems.forEach((item) => {
    const key = buildStockIdentity(item);
    const existing = seen.get(key);

    if (!existing) {
      seen.set(key, item);
      return;
    }

    seen.set(key, mergeDuplicateStockItems(existing, item));
  });

  return Array.from(seen.values());
}

function normalizeStockResponse(stockItems, contactId) {
  if (!Array.isArray(stockItems)) return [];
  const deduped = dedupeStockItems(stockItems);
  return deduped.map((item) => normalizeStockItem(item, contactId));
}

export function useStockItems(params = {}) {
  const useMockStockData = shouldUseMockStockData();
  const { contactId, gateOpen, gatePending } = useContactGate({
    bypass: useMockStockData,
  });

  const query = useQuery(
    // The backend returns isLiked as a per-contactId map (household/IBO can
    // have multiple contacts), so contactId is genuine cache-key data, not
    // decoration. It resolves asynchronously (starts null), so we gate with
    // enabled below instead of firing early and normalizing against null.
    ["stockItems", params, useMockStockData, contactId],
    async () => {
      if (useMockStockData) {
        return { categories: [], stockItems: enhancedStockInfo };
      }

      const query = new URLSearchParams(params).toString();
      const res = await makeAuthedRequest(
        `/stock-items${query ? `?${query}` : ""}`
      );
      if (!res.ok) throw await parseApiError(res, "StockItems fetch failed");
      const apiResponse = await res.json();

      // Handle both old format (array) and new format ({ categories, stockItems })
      if (Array.isArray(apiResponse)) {
        return {
          categories: [],
          stockItems: normalizeStockResponse(apiResponse, contactId),
        };
      }

      const { categories = [], stockItems = [] } = apiResponse;
      return {
        categories,
        stockItems: normalizeStockResponse(stockItems, contactId),
      };
    },
    { enabled: gateOpen }
  );

  // While the enabled gate above holds the query pending on contactId,
  // react-query reports status "idle" (isLoading false), not "loading".
  // Consumers that only check isLoading would otherwise flash an empty/
  // not-found state before the fetch even starts, so fold isIdle in here -
  // using gatePending verbatim (see useContactGate) so this can never drift
  // out of sync with the enabled gate above, and so we stop folding once
  // contactId has settled to "no usable value" instead of hanging
  // isLoading: true forever.
  return {
    ...query,
    isLoading: query.isLoading || (query.isIdle && gatePending),
  };
}

export function useStockItem(stockId, options = {}) {
  const { enabled = true } = options;
  const useMockStockData = shouldUseMockStockData();
  const { contactId, gateOpen, gatePending } = useContactGate({
    bypass: useMockStockData,
  });

  const query = useQuery(
    // See useStockItems: isLiked is a per-contactId map from the backend, so
    // contactId is real cache-key data and we gate on it below rather than
    // firing before it resolves.
    ["stockItem", stockId, useMockStockData, contactId],
    async () => {
      if (useMockStockData) {
        return (
          enhancedStockInfo.find(
            (item) => String(item.stockId) === String(stockId)
          ) || null
        );
      }

      const res = await makeAuthedRequest(`/stock-items/${stockId}`);
      if (!res.ok) throw await parseApiError(res, "StockItem fetch failed");
      const stockItem = await res.json();
      return normalizeStockItem(stockItem, contactId);
    },
    { enabled: enabled && !!stockId && gateOpen }
  );

  // See useStockItems: fold isIdle into isLoading using gatePending verbatim
  // so it can never drift from the enabled gate above. An intentionally-
  // disabled query (enabled: false) or missing stockId is excluded from the
  // fold so it reaches a real terminal state instead of hanging.
  return {
    ...query,
    isLoading:
      query.isLoading || (query.isIdle && enabled && !!stockId && gatePending),
  };
}
